import {
  createAsyncThunk,
  createListenerMiddleware,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import staffApi from "../api/staffApi";
import normalizeAxiosError from "../utils/normalizeAxiosError";
import {
  clearTokens,
  loadTokensFromStorage,
  setTokens,
} from "../utils/tokenStorage";

const persistedTokens = loadTokensFromStorage();

const initialState = {
  accessToken: persistedTokens.access,
  refreshToken: persistedTokens.refresh,
  isLogged: Boolean(persistedTokens.access),
  status: "idle",
  error: null,
};

const applyLoggedOutState = (state) => {
  state.accessToken = null;
  state.refreshToken = null;
  state.isLogged = false;
};

export const loginStaff = createAsyncThunk(
  "staff/login",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const data = await staffApi.login({ username, password });
      const { access, refresh } = data ?? {};

      if (!access || !refresh) {
        return rejectWithValue("Réponse de connexion invalide");
      }

      return { access, refresh };
    } catch (err) {
      return rejectWithValue(
        normalizeAxiosError(err, "Identifiants de connexion invalides")
      );
    }
  }
);

export const logoutStaff = createAsyncThunk(
  "staff/logout",
  async (_, { getState, rejectWithValue }) => {
    try {
      const refreshToken = getState().staff?.refreshToken;

      if (!refreshToken) {
        return { detail: "Session déjà fermée" };
      }

      await staffApi.logout(refreshToken);
      return { detail: "Déconnexion réussie" };
    } catch (err) {
      if (err.response?.status === 401) {
        return { detail: "Session expirée" };
      }
      return rejectWithValue(
        normalizeAxiosError(err, "Erreur lors de la déconnexion")
      );
    }
  }
);

const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    logout(state) {
      applyLoggedOutState(state);
      state.status = "idle";
      state.error = null;
    },
    clearStaffError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginStaff.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loginStaff.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.accessToken = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.isLogged = true;
      })
      .addCase(loginStaff.rejected, (state, action) => {
        state.status = "failed";
        applyLoggedOutState(state);
        state.error = action.payload ?? "Identifiants de connexion invalides";
      })
      .addCase(logoutStaff.pending, (state) => {
        state.status = "loading";
      })
      .addCase(logoutStaff.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
        applyLoggedOutState(state);
      })
      .addCase(logoutStaff.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Impossible de se déconnecter";
      });
  },
});

export const staffAuthListener = createListenerMiddleware();

staffAuthListener.startListening({
  actionCreator: loginStaff.fulfilled,
  effect: async (action) => {
    setTokens({
      access: action.payload.access,
      refresh: action.payload.refresh,
    });
  },
});

staffAuthListener.startListening({
  matcher: isAnyOf(staffSlice.actions.logout, logoutStaff.fulfilled),
  effect: async () => {
    clearTokens();
  },
});

export const { logout, clearStaffError } = staffSlice.actions;

export const selectStaffState = (state) => state.staff;
export const selectStaffStatus = (state) => state.staff.status;
export const selectStaffError = (state) => state.staff.error;
export const selectStaffAccessToken = (state) => state.staff.accessToken;
export const selectStaffRefreshToken = (state) => state.staff.refreshToken;
export const selectIsStaffLogged = (state) => state.staff.isLogged;

export default staffSlice;
